import cx from 'classnames';
import { useRef } from 'react';

import { Button, Modal } from '@sb/design-system';

import type { ChatHistorySession } from './ChatHistorySession';

export interface DeleteSessionModalProps {
  /**
   * Session pending deletion, or null when no deletion is in flight.
   * Null keeps the modal closed.
   */
  session: ChatHistorySession | null;
  /** Confirms the deletion. Parent persists the soft-delete to feathers. */
  onConfirm: () => void;
  /** Closes the modal without deleting. Also fires on backdrop / Escape. */
  onCancel: () => void;
  /**
   * Disables the buttons while the delete is in flight. Prevents a
   * second tap from re-firing onConfirm.
   */
  isDeleting?: boolean;
}

/**
 * Confirmation modal for chat-session deletion. Matches the routine-
 * delete dialog: a centered question + No / Yes, delete buttons.
 * Unlike the in-bubble action cards, this is a hard stop until the
 * user picks. Idempotent retries (server returned 404 because the
 * row was already gone) belong in the parent's delete handler.
 */
export function DeleteSessionModal({
  session,
  onConfirm,
  onCancel,
  isDeleting = false,
}: DeleteSessionModalProps) {
  // `Dialog` keeps its children mounted through the close fade, so reading
  // the title straight from `session` swaps in the fallback text mid-animation
  // as soon as the parent clears the pending session.
  const lastTitle = useRef('this chat');

  if (session !== null) {
    lastTitle.current = session.title;
  }

  const title = lastTitle.current;

  return (
    <Modal
      isOpen={session !== null}
      onClose={onCancel}
      aria-label="Delete chat session"
      data-testid="chat-history-delete-session-modal"
    >
      <div
        className={cx(
          'tw-flex',
          'tw-flex-col',
          'tw-gap-16',
          'tw-p-24',
          'tw-min-w-[320px]',
          'tw-max-w-[420px]',
        )}
      >
        <h2
          className={cx(
            'tw-text-17',
            'tw-font-medium',
            'tw-text-label-primary',
          )}
        >
          Delete this chat?
        </h2>
        <p className={cx('tw-m-0', 'tw-text-13', 'tw-text-label-secondary')}>
          “{title}” will be removed from your chat history. This can&apos;t be
          undone.
        </p>
        <div className={cx('tw-flex', 'tw-justify-end', 'tw-gap-8')}>
          <Button
            type="button"
            variant="Gray"
            size={36}
            onClick={onCancel}
            disabled={isDeleting}
            data-testid="chat-history-delete-session-cancel-button"
          >
            No
          </Button>
          <Button
            type="button"
            variant="Filled"
            color="Red"
            size={36}
            onClick={onConfirm}
            disabled={isDeleting}
            data-testid="chat-history-delete-session-confirm-button"
          >
            Yes, delete
          </Button>
        </div>
      </div>
    </Modal>
  );
}
